import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import ErrorState from '@/components/ErrorState';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { divinationLabel } from '@/data/divination';
import { reqNo } from '@/data/format';
import { birthDateText, birthTimeText } from '@/data/birth';
import { hasContactInfo } from '@/data/contact';
import { toCode, symbolOf } from '@/data/money';

const SubmitAnswer = () => {
  const { requestId } = useParams();
  const { user, config } = useAuth();
  const free = config?.free_mode;
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [observation, setObservation] = useState('');
  const [scopeQuotes, setScopeQuotes] = useState({});
  // Empty until the profile loads — a wrong-currency label (e.g. NT$ for an HK$
  // astrologer) is a real-money mislabel, so show nothing rather than a guess.
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRequest();
    // Surface the astrologer's own quoting currency on the price inputs.
    axios.get(`${API}/astrologer/profile`, { withCredentials: true })
      .then((r) => setCurrencySymbol(symbolOf(toCode(r.data?.price_currency))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const fetchRequest = async () => {
    try {
      const response = await axios.get(`${API}/requests/${requestId}`, {
        withCredentials: true
      });
      setRequest(response.data);
      setAnswers(response.data.verification_questions.map(q => ({
        question_order: q.order,
        answer_text: ''
      })));
      const sc = (response.data.scopes && response.data.scopes.length) ? response.data.scopes : ['整體'];
      const initQ = {};
      sc.forEach((s) => { initQ[s] = { price: '', minutes: '' }; });
      setScopeQuotes(initQ);
    } catch (error) {
      console.error('Error fetching request:', error);
      toast.error('無法載入求測單');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (index, value) => {
    const newAnswers = [...answers];
    newAnswers[index].answer_text = value;
    setAnswers(newAnswers);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (answers.some(a => !a.answer_text.trim())) {
      toast.error('請回答所有問題');
      return;
    }

    if (answers.some(a => a.answer_text.length > 50)) {
      toast.error('每題回答不可超過 50 字');
      return;
    }

    if (answers.some(a => hasContactInfo(a.answer_text)) || hasContactInfo(observation)) {
      toast.error('回答不可包含聯絡方式（電話、Email、社交帳號等），請移除後再提交');
      return;
    }

    if (observation.length > 500) {
      toast.error('補充觀察不能超過 500 字');
      return;
    }

    const scopeList = (request.scopes && request.scopes.length) ? request.scopes : ['整體'];
    const quotes = scopeList.map((s) => ({
      scope: s,
      price: parseInt(scopeQuotes[s]?.price),
      consultation_time_minutes: parseInt(scopeQuotes[s]?.minutes),
    }));
    if (quotes.some((q) => !q.price || q.price <= 0 || !q.consultation_time_minutes || q.consultation_time_minutes <= 0)) {
      toast.error('請為每個批算範圍填寫有效的價錢同時間');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/answers`,
        {
          request_id: requestId,
          answers,
          observation,
          quotes,
        },
        { withCredentials: true }
      );
      
      toast.success('回答已提交');
      navigate('/astrologer/requests');
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error(error.response?.data?.detail || '提交失敗');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg">載入中...</p>
      </div>
    );
  }

  if (!request) {
    return <ErrorState message="找不到求測單" />;
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-[#E5E0D8] px-6 md:px-12 lg:px-24 py-4">
        <Link to="/astrologer/dashboard">
          <h2 className="text-xl font-serif text-[#C93B2B]">命理配對平台</h2>
        </Link>
      </nav>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <Link to="/astrologer/requests">
          <Button variant="ghost" className="mb-6 rounded-sm" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            返回
          </Button>
        </Link>

        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#1A1A1A] mb-2">
            回答驗證問題
          </h1>
          <p className="text-base text-[#5C5C5C] mb-4">
            根據求測者的出生資料來回答問題
          </p>

          <div className="bg-[#FBF7EF] border border-[#E8D9BF] rounded-sm p-4 mb-8 text-sm leading-relaxed text-[#5C5C5C]" data-testid="grading-notice">
            <p className="font-medium text-[#1A1A1A] mb-1">關於評核</p>
            你嘅回答會由系統自動就<strong className="font-medium text-[#1A1A1A]">準確度</strong>同<strong className="font-medium text-[#1A1A1A]">具體程度</strong>，對照求測者親自提供嘅真實情況作評核，計入你嘅「準度」。具體、貼題嘅回答分數較高；空泛或人人適用嘅內容分數較低。
            <span className="block mt-1">答得好嘅回報：畀求測者揀中就開通諮詢，仲會累積你嘅準度往績。</span>
          </div>

          <div className="bg-white border border-[#E5E0D8] rounded-sm p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="inline-flex items-center border border-[#C93B2B] text-[#C93B2B] px-2 py-1 text-xs rounded-none font-serif tracking-widest">
                {divinationLabel(request)}
              </div>
              {request.seq && <span className="text-xs text-[#9C9C9C] font-mono">{reqNo(request.seq)}</span>}
            </div>
            
            <div className="mb-4">
              <p className="text-xs text-[#5C5C5C] mb-1">求測者</p>
              <p className="text-sm font-medium text-[#1A1A1A]">{request.client_name}</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-[#5C5C5C] mb-1">出生日期</p>
                <p className="text-sm font-medium text-[#1A1A1A]">{birthDateText(request)}</p>
              </div>
              <div>
                <p className="text-xs text-[#5C5C5C] mb-1">出生時間</p>
                <p className="text-sm font-medium text-[#1A1A1A]">{birthTimeText(request)}</p>
              </div>
              <div>
                <p className="text-xs text-[#5C5C5C] mb-1">性別</p>
                <p className="text-sm font-medium text-[#1A1A1A]">{request.gender === 'male' ? '男' : '女'}</p>
              </div>
              <div>
                <p className="text-xs text-[#5C5C5C] mb-1">出生地區</p>
                <p className="text-sm font-medium text-[#1A1A1A]">{request.region}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {request.verification_questions.map((q, index) => (
              <div key={`q-${q.order}`} className="bg-white border border-[#E5E0D8] rounded-sm p-6 md:p-8">
                <div className="bg-[#F5F0E6] rounded-sm p-4 mb-4">
                  <p className="text-xs text-[#C93B2B] font-bold mb-1">問題 {q.order}</p>
                  <p className="text-sm text-[#1A1A1A]">{q.question_text}</p>
                </div>
                
                <Label htmlFor={`answer-${index}`}>你的回答 * (最多 50 字)</Label>
                <Textarea
                  id={`answer-${index}`}
                  value={answers[index].answer_text}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  placeholder="根據命理分析回答這個問題..."
                  className="rounded-sm mt-2"
                  rows={3}
                  maxLength={50}
                  required
                  data-testid={`answer-${index}`}
                />
                <p className="text-xs text-[#5C5C5C] mt-1 text-right">
                  {answers[index].answer_text.length} / 50
                </p>
              </div>
            ))}

            <div className="bg-white border border-[#E5E0D8] rounded-sm p-6 md:p-8">
              <Label htmlFor="observation">補充觀察 (選填，最多 500 字)</Label>
              <Textarea
                id="observation"
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                placeholder="加入你的簡短補充觀察..."
                className="rounded-sm mt-2"
                rows={5}
                maxLength={500}
                data-testid="observation-input"
              />
              <p className="text-xs text-[#5C5C5C] mt-1 text-right">
                {observation.length} / 500
              </p>
            </div>

            <div className="bg-white border border-[#E5E0D8] rounded-sm p-6 md:p-8">
              <h3 className="text-xl font-serif text-[#1A1A1A] mb-2">報價{free ? '（參考價）' : ''}</h3>
              <p className="text-sm text-[#5C5C5C] mb-4">
                {free
                  ? '就求測者所選的每個批算範圍，分別填寫價錢同諮詢時間。平台目前免費開通諮詢，報價只供求測者參考，日後平台收費時先會生效。'
                  : '就求測者所選的每個批算範圍，分別填寫價錢同諮詢時間。'}
              </p>

              {((request.scopes && request.scopes.length) ? request.scopes : ['整體']).map((s) => (
                <div key={`quote-${s}`} className="mb-5 last:mb-0">
                  <p className="text-sm font-medium text-[#C93B2B] mb-2">{s}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor={`time-${s}`}>諮詢時間 (分鐘) *</Label>
                      <Input
                        id={`time-${s}`}
                        type="number"
                        min="1"
                        value={scopeQuotes[s]?.minutes || ''}
                        onChange={(e) => setScopeQuotes((p) => ({ ...p, [s]: { ...(p[s] || {}), minutes: e.target.value } }))}
                        placeholder="例：60"
                        className="rounded-sm mt-2"
                        required
                        data-testid={`time-input-${s}`}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`price-${s}`}>價格{currencySymbol ? `（${currencySymbol}）` : ''} *</Label>
                      <Input
                        id={`price-${s}`}
                        type="number"
                        min="1"
                        value={scopeQuotes[s]?.price || ''}
                        onChange={(e) => setScopeQuotes((p) => ({ ...p, [s]: { ...(p[s] || {}), price: e.target.value } }))}
                        placeholder="例：2000"
                        className="rounded-sm mt-2"
                        required
                        data-testid={`price-input-${s}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-[#FEF8F0] border border-[#C93B2B] rounded-sm p-4">
              <p className="text-sm text-[#1A1A1A] font-medium">
                ⚠️ 提醒：答案有字數限制，避免提供過多免費內容。{free
                  ? '求測者選定你之後，你們可以在私訊中進行更詳細的諮詢。'
                  : '求測者選定並付款後，你們可以在私訊中進行更詳細的諮詢。'}
              </p>
            </div>

            <div className="flex justify-end gap-4">
              <Link to="/astrologer/requests">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="rounded-sm border-[#E5E0D8]"
                  data-testid="cancel-btn"
                >
                  取消
                </Button>
              </Link>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm shadow-sm"
                data-testid="submit-answer-btn"
              >
                {submitting ? '提交中...' : '提交回答'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubmitAnswer;